import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import sixLoveMark from '../../assets/6love-mark.svg';
import './landing.css';

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: string[] };

type Section = { h: string; blocks: Block[] };

const p = (text: string): Block => ({ kind: 'p', text });
const ul = (items: string[]): Block => ({ kind: 'list', items });

const PRIVACY_INTRO =
  'This Privacy Policy describes how 6-love ("6-love", "we", "us", or "our") collects, uses, shares, and protects information when you use our website and application (the "Service"). By using the Service, you agree to the collection and use of information as described in this policy.';

const PRIVACY: Section[] = [
  {
    h: 'Information we collect',
    blocks: [
      ul([
        'Account information: your name and email address, and a password if you sign up directly, or your Google account identifier and profile photo if you sign in with Google.',
        'Team, match, and scheduling data: team names, rosters, and roles; match schedules, locations, and opponents; availability you and others submit; lineups, scores, and results; and any notes or messages shared within your teams.',
        'AI Co-Captain interactions: when you use the Co-Captain, we send the relevant team and match context, along with your question, to our AI provider(s) to generate a response, and we may retain a record of these interactions to provide and improve the feature.',
        'Usage and device information: we use analytics tools (such as PostHog) to collect information about the pages and features you use, actions you take, your browser and device type, and an approximate location derived from your IP address.',
        'Communications: if you contact us for support, we collect the information you provide, such as your email address and the content of your message.',
      ]),
    ],
  },
  {
    h: 'How we use your information',
    blocks: [
      ul([
        'Provide, maintain, and improve the Service, including team management, scheduling, availability, and lineup features.',
        'Authenticate you and keep your account secure.',
        'Send notifications and messages you’ve opted into, such as match reminders, availability requests, and lineup confirmations.',
        'Generate AI Co-Captain suggestions based on your team’s data.',
        'Analyze usage trends to improve the Service’s design, performance, and reliability.',
        'Detect, investigate, and prevent fraud, abuse, or unauthorized activity.',
        'Process payments and manage subscriptions, if you choose a paid plan.',
        'Communicate with you about updates, security notices, and support requests.',
        'Comply with our legal obligations.',
      ]),
    ],
  },
  {
    h: 'Cookies and similar technologies',
    blocks: [
      p('We and our service providers use cookies, local storage, and similar technologies to keep you signed in, remember your preferences, and understand how the Service is used. You can control cookies through your browser settings, though disabling them may limit some features of the Service.'),
    ],
  },
  {
    h: 'How we share information',
    blocks: [
      ul([
        'With your teammates: information you provide — such as your name, availability, and lineup assignments — may be visible to other members of the teams you belong to, as part of the normal operation of the Service.',
        'Service providers: we share information with vendors who perform services on our behalf, including cloud hosting (Google Cloud Platform), authentication (Firebase), email delivery (SendGrid), product analytics (PostHog), payment processing (Stripe), and the AI providers used for the Co-Captain feature. These providers may use your information only to provide services to us.',
        'Legal and safety: we may disclose information if required by law or legal process, or if we believe it’s necessary to protect the rights, property, or safety of 6-love, our users, or others.',
        'Business transfers: if 6-love is involved in a merger, acquisition, financing, or sale of assets, your information may be transferred as part of that transaction, subject to the protections described in this policy.',
      ]),
      p('We do not sell your personal information.'),
    ],
  },
  {
    h: 'Data retention',
    blocks: [
      p('We retain your information for as long as your account is active or as needed to provide the Service. If you request deletion of your account, we’ll delete or de-identify your personal information within a reasonable period, except where we’re required or permitted to retain it for legal, security, or legitimate business purposes.'),
    ],
  },
  {
    h: 'Data security',
    blocks: [
      p('We use administrative, technical, and physical safeguards designed to protect your information from unauthorized access, use, or disclosure. No method of transmission over the internet or electronic storage is completely secure, and we can’t guarantee absolute security.'),
    ],
  },
  {
    h: 'Your rights and choices',
    blocks: [
      p('Depending on where you live, you may have rights under applicable data protection laws — such as the GDPR or the CCPA/CPRA — to access, correct, delete, or receive a copy of your personal information, and to object to or restrict certain uses of it. You can update most account details directly in the Service and manage your notification preferences from your profile settings. To request deletion of your account, a copy of your data, or to exercise other privacy rights, contact us at support@6love.app, and we’ll respond within the timeframe required by applicable law.'),
    ],
  },
  {
    h: 'International data transfers',
    blocks: [
      p('6-love is based in the United States and the Service is hosted on infrastructure located in the United States. If you access the Service from outside the United States, your information will be transferred to, and processed and stored in, the United States or other countries where our service providers operate, which may have data protection laws that differ from those in your jurisdiction.'),
    ],
  },
  {
    h: 'Children’s privacy',
    blocks: [
      p('The Service isn’t directed to, and we don’t knowingly collect personal information from, children under 13 (or the minimum age required by your jurisdiction). If you believe a child has provided us with personal information, please contact us so we can delete it.'),
    ],
  },
  {
    h: 'Changes to this policy',
    blocks: [
      p('We may update this Privacy Policy from time to time. If we make material changes, we’ll update the "Last updated" date below and, where appropriate, provide additional notice such as an email or in-app notification. Your continued use of the Service after a change takes effect means you accept the updated policy.'),
    ],
  },
  {
    h: 'Contact us',
    blocks: [
      p('Questions about this Privacy Policy or how we handle your information? Email us at support@6love.app.'),
    ],
  },
];

const TERMS_INTRO =
  'These Terms of Service ("Terms") are an agreement between you and 6-love ("6-love", "we", "us", or "our") governing your access to and use of our website and application (the "Service"). By creating an account or otherwise using the Service, you agree to these Terms and to our Privacy Policy. If you don’t agree, please don’t use the Service.';

const TERMS: Section[] = [
  {
    h: 'Eligibility',
    blocks: [
      p('You must be at least 13 years old, or the minimum age required to use online services in your jurisdiction, to use the Service. By using the Service, you confirm that you meet this requirement and have the legal capacity to enter into these Terms.'),
    ],
  },
  {
    h: 'Your account',
    blocks: [
      p('You’re responsible for providing accurate account information, keeping your login credentials confidential, and for all activity that happens under your account. Let us know promptly at support@6love.app if you suspect any unauthorized use of your account.'),
    ],
  },
  {
    h: 'Teams and shared data',
    blocks: [
      p('The Service lets you create and join teams. Captains can manage rosters, schedules, and lineups; players can view team information and set their own availability. Information you submit — including your name, availability, and lineup assignments — may be visible to other members of your teams. You’re responsible for the accuracy of the information you and your team enter.'),
    ],
  },
  {
    h: 'Acceptable use',
    blocks: [
      p('When using the Service, you agree not to:'),
      ul([
        'Use the Service for any unlawful purpose or in violation of any applicable law or regulation.',
        'Upload or share content that is harassing, abusive, defamatory, obscene, or that violates the rights of others.',
        'Attempt to gain unauthorized access to the Service, other users’ accounts, or any related systems or networks.',
        'Interfere with or disrupt the integrity or performance of the Service, including by introducing malware or excessive automated traffic.',
        'Reverse-engineer, decompile, or attempt to extract the source code of the Service, except as permitted by law.',
        'Scrape, harvest, or collect data from the Service without our prior written consent.',
        'Use the Service to send unsolicited communications.',
      ]),
    ],
  },
  {
    h: 'AI Co-Captain',
    blocks: [
      p('The Service includes an AI-powered "Co-Captain" feature that can suggest lineups, scheduling options, and other team-management recommendations based on the information available to it. These suggestions are provided for convenience only — they may be incomplete, inaccurate, or unsuitable for your situation. You and your team’s captains remain solely responsible for reviewing, editing, and approving any lineup, message, or decision before relying on it or sharing it with others.'),
    ],
  },
  {
    h: 'Fees and subscriptions',
    blocks: [
      p('Some features of the Service are available free of charge. We may also offer paid subscription plans that unlock additional capacity or features. If you purchase a subscription, the price, billing frequency, and renewal terms will be presented to you before you’re charged. Payments are processed by our third-party payment processor, Stripe, and you can manage or cancel your subscription at any time through your account settings or the Stripe customer portal. Fees are non-refundable except as required by law or as we expressly state.'),
    ],
  },
  {
    h: 'Third-party services',
    blocks: [
      p('The Service relies on and integrates with third-party services — for example, Google for sign-in and Stripe for payments. Your use of those services is also subject to their own terms and privacy policies, and we’re not responsible for their content, availability, or practices.'),
    ],
  },
  {
    h: 'Intellectual property',
    blocks: [
      p('The Service, including its software, design, text, graphics, and logos, is owned by 6-love or our licensors and is protected by intellectual property laws. Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for its intended purpose. You retain ownership of the content and information you submit to the Service ("User Content"). You grant us a worldwide, non-exclusive, royalty-free license to host, store, reproduce, and display your User Content solely as needed to provide and improve the Service, including sharing it with your teammates as contemplated by these Terms.'),
    ],
  },
  {
    h: 'Disclaimers',
    blocks: [
      p('To the fullest extent permitted by law, the Service is provided on an "as is" and "as available" basis, without warranties of any kind, whether express, implied, or statutory — including implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement. We don’t warrant that the Service will be uninterrupted, secure, or error-free, or that any schedule, lineup, or AI-generated suggestion will be accurate, complete, or suitable for your needs.'),
    ],
  },
  {
    h: 'Limitation of liability',
    blocks: [
      p('To the fullest extent permitted by law, 6-love and its officers, employees, and service providers won’t be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any loss of profits, revenue, data, or goodwill, arising from or relating to your use of (or inability to use) the Service — even if we’ve been advised of the possibility of such damages. Our total liability for any claim arising out of or relating to these Terms or the Service won’t exceed the greater of (a) the amount you’ve paid us for the Service in the 12 months before the claim arose, or (b) $50.'),
    ],
  },
  {
    h: 'Indemnification',
    blocks: [
      p('You agree to defend, indemnify, and hold harmless 6-love and its officers, employees, and service providers from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees) arising out of or related to your use of the Service, your User Content, or your violation of these Terms or applicable law.'),
    ],
  },
  {
    h: 'Termination',
    blocks: [
      p('You can stop using the Service and request deletion of your account at any time by contacting us at support@6love.app. We may suspend or terminate your access if we reasonably believe you’ve violated these Terms, to comply with legal requirements, or to protect the Service or other users, with or without notice where permitted by law. Sections of these Terms that by their nature should survive termination — including intellectual property, disclaimers, limitation of liability, and indemnification — will continue to apply.'),
    ],
  },
  {
    h: 'Governing law and disputes',
    blocks: [
      p('These Terms are governed by the laws of the State of California, without regard to its conflict-of-laws principles. Any dispute arising out of or relating to these Terms or the Service will be subject to the exclusive jurisdiction of the state or federal courts located in California, and you consent to personal jurisdiction in those courts.'),
    ],
  },
  {
    h: 'Changes to these terms',
    blocks: [
      p('We may update these Terms from time to time. If we make material changes, we’ll provide reasonable notice — such as an email or an in-app notification — before the changes take effect. Your continued use of the Service after the changes take effect means you accept the updated Terms.'),
    ],
  },
  {
    h: 'General',
    blocks: [
      p('If any provision of these Terms is found unenforceable, the remaining provisions will remain in full effect. Our failure to enforce any right or provision won’t be treated as a waiver of that right or provision. These Terms, together with our Privacy Policy, make up the entire agreement between you and 6-love regarding the Service.'),
    ],
  },
  {
    h: 'Contact us',
    blocks: [
      p('Questions about these Terms? Email us at support@6love.app.'),
    ],
  },
];

const DOCS = {
  privacy: { title: 'Privacy Policy', intro: PRIVACY_INTRO, sections: PRIVACY },
  terms: { title: 'Terms of Service', intro: TERMS_INTRO, sections: TERMS },
};

const LegalPage: React.FC<{ kind: 'privacy' | 'terms' }> = ({ kind }) => {
  const doc = DOCS[kind];
  useEffect(() => { window.scrollTo(0, 0); }, [kind]);

  return (
    <div className="lp-page">
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <Link to="/" aria-label="6-love home" style={{ display: 'inline-flex' }}>
            <span className="lp-wordmark" style={{ gap: 9 }}>
              <img src={sixLoveMark} alt="" style={{ width: 30, height: 30 }} />
              <span className="lp-wordmark-text" style={{ fontSize: 20 }}><span className="six">6</span>-love</span>
            </span>
          </Link>
          <Link to="/" className="lp-nav-login" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={16} /> Back to home
          </Link>
        </div>
      </header>

      <main className="lp-legal">
        <h1 className="lp-legal-h1">{doc.title}</h1>
        <p className="lp-legal-meta">Last updated June 2026</p>
        <p className="lp-legal-p" style={{ marginTop: 20 }}>{doc.intro}</p>
        {doc.sections.map((s, i) => (
          <section key={s.h} className="lp-legal-section">
            <h2 className="lp-legal-h2">{i + 1}. {s.h}</h2>
            {s.blocks.map((b, j) => (
              b.kind === 'p'
                ? <p key={j} className="lp-legal-p">{b.text}</p>
                : (
                  <ul key={j} className="lp-legal-ul">
                    {b.items.map((item, k) => <li key={k} className="lp-legal-li">{item}</li>)}
                  </ul>
                )
            ))}
          </section>
        ))}
      </main>
    </div>
  );
};

export default LegalPage;
